import React from "react";
import { Link } from "react-router-dom";
import { BRAND, NAV_LINKS } from "../config/brand";
import { useLang } from "../i18n/LangContext";

export const Footer = () => {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/5 bg-black pt-20 pb-10"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 pb-16 border-b border-white/5">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={BRAND.logo}
                alt="LionPerformance"
                className="h-12 w-12 object-contain"
                style={{ filter: "url(#lp-remove-black)" }}
              />
              <span className="font-display text-2xl font-bold tracking-tight text-white">
                Lion<span className="text-primary-red">Performance</span>
              </span>
            </div>
            <p className="font-body text-sm text-zinc-400 leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 mb-5">
              {t("footer.nav")}
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => scrollTo(l.id)}
                    className="text-sm text-zinc-300 hover:text-primary-red transition-colors"
                  >
                    {t(l.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 mb-5">
              {t("footer.legal")}
            </div>
            <ul className="space-y-3">
              <li>
                <Link
                  data-testid="footer-privacy"
                  to="/datenschutz"
                  className="text-sm text-zinc-300 hover:text-primary-red transition-colors"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-agb"
                  to="/agb"
                  className="text-sm text-zinc-300 hover:text-primary-red transition-colors"
                >
                  AGB
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-imprint"
                  to="/impressum"
                  className="text-sm text-zinc-300 hover:text-primary-red transition-colors"
                >
                  {t("footer.imprint")}
                </Link>
              </li>
              <li>
                <button
                  data-testid="footer-contact"
                  onClick={() => scrollTo("kontakt")}
                  className="text-sm text-zinc-300 hover:text-primary-red transition-colors"
                >
                  {t("footer.contact")}
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 mb-5">
              {t("footer.social")}
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="mailto:info@lionperformance.fit"
                  data-testid="footer-email"
                  className="text-zinc-300 hover:text-primary-red transition-colors break-all"
                >
                  info@lionperformance.fit
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/lionperformance/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="text-zinc-300 hover:text-primary-red transition-colors"
                >
                  Instagram — @lionperformance
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/marc-wiebeck/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin"
                  className="text-zinc-300 hover:text-primary-red transition-colors"
                >
                  LinkedIn — Marc Wiebeck
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-zinc-600">
            © {new Date().getFullYear()} LionPerformance. {t("footer.copyright")}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-700">
            Elite Sports Psychology · Coaching Worldwide
          </div>
        </div>
      </div>
    </footer>
  );
};
